import { defineStore } from 'pinia';

import {
  getImage,
  IImage,
  ImageClass,
  initializeImages,
  resetImages,
} from '../common.js';
import Tr from '@/i18n/translation';
import { useStateStore } from '@/stores/state.store';
import { DataService } from '@/services/index.js';
import ImageService from '../../services/ImageService.js';
import { Logger } from '../../middlewares/sentry.js';
import { IEventImagesStructure, IEventState } from './event.state';
import { EventCommonClass } from 'interface/data/event/common';
import { EventLangClass } from 'interface/data/event/lang';
import { ImageType } from 'interface/data/image/type.js';
import { EventDataClass } from 'interface/data/event/data.js';

class EventImagesStructureClass implements IEventImagesStructure {
  [ImageType.EVENTBACKGROUND]: IImage = new ImageClass();
  [ImageType.EVENTPICTURE]: IImage = new ImageClass();
  [ImageType.EVENTADDITIONALSITES]: IImage[] = [];
}

export const useEventStore = defineStore('event', {
  state: (): IEventState => ({
    clientImages: initializeImages(EventImagesStructureClass),
    clientData: new EventDataClass(),
    isDataReceived: false,
    isLangDataReceived: false,
  }),
  actions: {
    async trySetupData() {
      await this._setEventCommonData();
      const lang = Tr.currentLocale;
      this.tryGetLangDataWithImages(lang);
    },
    async _setEventCommonData() {
      if (!this.isDataReceived) {
        await this._tryGetEventCommonData();
        this.isDataReceived = true;
      } else {
        new Promise(async () => {
          this._tryGetEventCommonData();
        });
      }

      const { setColors } = useStateStore();
      setColors(
        this.clientData.common.mainColor,
        this.clientData.common.buttonColor,
        this.clientData.common.backgroundColor,
      );
    },
    async _tryGetEventCommonData() {
      try {
        const data = await DataService.getEventDataV2();
        this.clientData.common = new EventCommonClass(data);
      } catch (err) {
        Logger.error(err);
        if (!this.isDataReceived) {
          this.clientData.common =
            await this._fallbackToOldInterface(EventCommonClass);
        }
      }
    },
    async tryGetLangDataWithImages(lang: string) {
      await this._setGetLangData(lang);
      this._tryGetClientImages(lang);
    },
    async _setGetLangData(lang: string) {
      if (!this.isLangDataReceived) {
        await this.tryGetLangData(lang);
        this.isLangDataReceived = true;
      } else {
        new Promise(async () => {
          this.tryGetLangData(lang);
        });
      }
    },
    async tryGetLangData(lang: string) {
      try {
        const data = await DataService.getEventDataV2(lang);
        this.clientData.lang[lang] = new EventLangClass(data);
      } catch (err) {
        Logger.error(err);
        if (!this.isLangDataReceived) {
          const { getDefaultLang } = useStateStore();

          if (lang !== getDefaultLang) {
            this.tryGetLangData(getDefaultLang);
            return;
          }

          this.clientData.lang[lang] =
            await this._fallbackToOldInterface(EventLangClass);
        }
      }
    },
    async _fallbackToOldInterface(Interface) {
      try {
        const data = await DataService.getEventData();
        return new Interface(data);
      } catch (err) {
        Logger.error(err);
        return new Interface({});
      }
    },
    async _tryGetClientImages(lang: string) {
      try {
        if (!this.clientImages[lang]) {
          this.clientImages[lang] = new EventImagesStructureClass();
        }

        const data = await ImageService.getImagesInfo();
        getImage(this.clientImages, data, ImageType.EVENTPICTURE, lang);
        getImage(this.clientImages, data, ImageType.EVENTBACKGROUND, lang);
        if (useEventStore().getAdditionalSites().common) {
          for (
            let i = 0;
            i < useEventStore().getAdditionalSites().common.length;
            ++i
          ) {
            getImage(
              this.clientImages,
              data,
              ImageType.EVENTADDITIONALSITES,
              lang,
              i,
            );
          }
        }
      } catch (err) {
        Logger.error(err);
      }
    },
    getAdditionalSites(lang: string = Tr.currentLocale) {
      return {
        common: this.clientData.common?.additionalSites ?? [],
        lang: this.getLangData('additionalSites', lang) ?? [],
      };
    },
    getPlaces(lang = Tr.currentLocale) {
      return {
        common: this.clientData.common.places ?? [],
        lang: this.getLangData('places', lang) ?? [],
      };
    },
    getAdditionalSitesById(id: number, lang: string = Tr.currentLocale) {
      const additionalSites = { common: {}, lang: {} };
      const commonAdditionalSites =
        this.clientData.common?.additionalSites || [];
      const langAdditionalSites =
        this.getLangData('additionalSites', lang) || [];
      if (id >= 0 && id < commonAdditionalSites.length) {
        additionalSites.common = commonAdditionalSites[id] ?? {};
      } else {
        Logger.warn(
          'Index out of range ' +
            id +
            ' common tab size ' +
            commonAdditionalSites.length,
        );
      }
      if (id >= 0 && id < langAdditionalSites.length) {
        additionalSites.lang = langAdditionalSites[id];
      } else {
        Logger.warn(
          'Index out of range ' +
            id +
            ' lang tab size ' +
            langAdditionalSites.length,
        );
      }

      return additionalSites;
    },
    getPlacesById(id: number, lang: string = Tr.currentLocale) {
      const place = { common: {}, lang: {} };
      const commonPlaces = this.clientData.common?.places || [];
      const langPlaces = this.getLangData('places', lang) || [];
      if (id >= 0 && id < commonPlaces.length) {
        place.common = commonPlaces[id] ?? {};
      } else {
        Logger.warn(
          'Index out of range ' +
            id +
            ' common tab size ' +
            commonPlaces.length,
        );
      }
      if (id >= 0 && id < langPlaces.length) {
        place.lang = langPlaces[id];
      } else {
        Logger.warn(
          'Index out of range ' + id + ' lang tab size ' + langPlaces.length,
        );
      }

      return place;
    },
    getLangData(data: string, lang: string = Tr.currentLocale) {
      if (!this.clientData.lang[lang]) {
        this.clientData.lang[lang] = new EventLangClass();
      }
      return this.clientData.lang[lang][data];
    },
    getLangAllLangData(lang: string = Tr.currentLocale) {
      if (!this.clientData.lang[lang]) {
        this.clientData.lang[lang] = new EventLangClass();
      }
      return this.clientData.lang[lang];
    },
    resetLangData(lang: string) {
      this.clientData.lang[lang] = new EventLangClass();
    },
    resetData() {
      this.clientData = new EventDataClass();
      resetImages(this.clientImages);
      this.isDataReceived = false;
      this.isLangDataReceived = false;
    },
  },
  getters: {
    isAnyPlaceCommon: (state) =>
      state.clientData &&
      state.clientData.common &&
      state.clientData.common.places &&
      state.clientData.common.places.length !== 0,
    isAnyPlaceLang: (state) =>
      state.clientData &&
      state.clientData.lang[Tr.currentLocale] &&
      state.clientData.lang[Tr.currentLocale]?.places &&
      state.clientData.lang[Tr.currentLocale]?.places.length !== 0,
    isAnyPlace(): boolean {
      return (this.isAnyPlaceCommon && this.isAnyPlaceLang) ?? false;
    },
    getCommonData: (state) => state.clientData.common,
    getImages: (state) => {
      const lang = Tr.currentLocale;
      if (!state.clientImages[lang]) {
        state.clientImages[lang] = new EventImagesStructureClass();
      }

      return state.clientImages[lang];
    },
    getColors: (state) => {
      return {
        mainColor: state.clientData.common.mainColor,
        buttonColor: state.clientData.common.buttonColor,
        backgroundColor: state.clientData.common.backgroundColor,
      };
    },
  },
});
